import AccountSubscriptionModel               from '@models/account/AccountSubscriptionModel';
import AccountSubscriptionPriceModel          from '@models/account/AccountSubscriptionPriceModel';
import RequestChangeSubscriptionCreateModel   from '@models/account/RequestChangeSubscriptionCreateModel';
import RequestChangeSubscriptionModel        from '@models/account/RequestChangeSubscriptionModel';
import SubscriptionModel                      from '@models/account/SubscriptionModel';
import SubscriptionPeriodModel                from '@models/account/SubscriptionPeriodModel';
import { acknowledgeClosedChange }            from '@/helpers/subscription/closedChangeStorage';
import { isFreePlan }                         from '@/helpers/subscription/plans';

export const requestSubscriptionCancellation = async (
	accountSubscription: AccountSubscriptionModel,
	freePlan: SubscriptionModel,
) => {
	const created = new RequestChangeSubscriptionCreateModel();

	await created.patch({
		accountSubscription: accountSubscription.urn,
		toSubscription: freePlan.urn,
	});

	return created.requestChangeSubscription?.scheduledAt;
};

/**
 * Abandonne une demande de changement encore en cours. Le DELETE ne supprime pas la demande : le
 * back la passe en « annulée », ferme le paiement éventuellement ouvert chez le prestataire, et
 * l'abonnement actuel se poursuit normalement.
 */
export const cancelSubscriptionChangeRequest = async (pendingChange: RequestChangeSubscriptionModel) => {
	// Le client sait ce qu'il vient de faire : on note la demande comme déjà lue pour ne pas la lui
	// annoncer ensuite comme un échec. C'est nécessaire parce que le prestataire, prévenu de
	// l'annulation, réécrit la demande derrière nous et en efface l'auteur.
	acknowledgeClosedChange(pendingChange.id);

	await pendingChange.destroy();
};

export type PlanChangeRequest = {
	accountSubscription: AccountSubscriptionModel;
	isUpgrade: boolean;
	negotiatedPrice?: AccountSubscriptionPriceModel;
	period?: SubscriptionPeriodModel;
	plan: SubscriptionModel;
};

export type PlanChangeOutcome =
	// Ces deux issues font sortir le client de l'application (page de paiement du prestataire, ou
	// écran d'attente). On transmet l'id de la demande pour pouvoir vérifier, au retour, si le
	// paiement a abouti.
	| { requestId?: id; type: 'redirect'; url: string }
	| { requestId?: id; type: 'activationPending' }
	| { scheduledAt?: Dayjs; type: 'scheduled' };

export const requestPlanChange = async (request: PlanChangeRequest): Promise<PlanChangeOutcome> => {
	const { accountSubscription, negotiatedPrice, period, plan } = request;

	if (isFreePlan(plan)) {
		return {
			scheduledAt: await requestSubscriptionCancellation(accountSubscription, plan),
			type: 'scheduled',
		};
	}

	const created = new RequestChangeSubscriptionCreateModel();

	await created.patch({
		accountSubscription: accountSubscription.urn,
		toSubscription: plan.urn,
		...(negotiatedPrice
			? { accountSubscriptionPrice: negotiatedPrice.urn }
			: { subscriptionPeriod: period?.urn }),
	});

	const requestId = created.requestChangeSubscription?.id;
	const redirectUrl = created.paymentUrl || created.authenticationUrl;

	if (redirectUrl) return { requestId, type: 'redirect', url: redirectUrl };

	if (request.isUpgrade) return { requestId, type: 'activationPending' };

	return { scheduledAt: created.requestChangeSubscription?.scheduledAt, type: 'scheduled' };
};

export const CANCELLATION_CONFIRM_MESSAGE = 'Votre abonnement basculera sur le plan gratuit à sa date anniversaire. Vous conservez l\'intégralité de vos fonctionnalités jusque-là.';

export const cancellationScheduledMessage = (scheduledAt?: Dayjs) => (
	scheduledAt?.isValid()
		? `Résiliation programmée le ${scheduledAt.format('DD/MM/YYYY')}. Votre accès reste complet jusque-là.`
		: 'Résiliation programmée à la date anniversaire de votre abonnement.'
);

export const scheduledChangeMessage = (scheduledAt?: Dayjs) => (
	scheduledAt?.isValid()
		? `Le changement prendra effet le ${scheduledAt.format('DD/MM/YYYY')}, à l'échéance de la période en cours.`
		: 'Le changement prendra effet à l\'échéance de la période en cours.'
);

export const planChangeBillingEffect = (request: Omit<PlanChangeRequest, 'negotiatedPrice' | 'period'>) => {
	const endDate = request.accountSubscription.end?.isValid()
		? request.accountSubscription.end.format('DD/MM/YYYY')
		: undefined;

	if (isFreePlan(request.plan)) {
		return endDate
			? `Prise d'effet le ${endDate}, à la date anniversaire de votre abonnement. Aucun prélèvement d'ici là.`
			: 'Prise d\'effet à la date anniversaire de votre abonnement. Aucun prélèvement d\'ici là.';
	}

	if (!request.accountSubscription.hasPaymentProviderSubscription) {
		return 'Vous serez redirigé vers le paiement sécurisé pour finaliser le changement.';
	}

	if (request.isUpgrade) {
		return 'Facturé immédiatement, au prorata de la période en cours. Votre banque peut vous demander de valider le paiement.';
	}

	return endDate
		? `Prise d'effet le ${endDate}, à l'échéance de la période en cours, sans prélèvement supplémentaire.`
		: 'Prise d\'effet à l\'échéance de la période en cours, sans prélèvement supplémentaire.';
};
